import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../lib/config';
import {
  ChevronLeftIcon,
  DocumentIcon
} from '@heroicons/react/24/outline';

const TermsOfService = () => {
  const navigate = useNavigate();
  const [cms, setCms] = useState({ title: '', content: '' });
  const [loading, setLoading] = useState(true);
  const ENABLE_CMS = process.env.REACT_APP_ENABLE_CMS === '1';

  useEffect(() => {
    if (!ENABLE_CMS) { setLoading(false); return; }
    let aborted = false;
    const load = async () => {
      try {
        const r = await fetch(`${API_BASE}/content/terms`);
        if (!r.ok) throw new Error('failed');
        const data = await r.json();
        if (!aborted) setCms({ title: data.title || '', content: data.content || '' });
      } catch (_) {
        if (!aborted) setCms({ title: '', content: '' });
      } finally {
        if (!aborted) setLoading(false);
      }
    };
    load();
    return () => { aborted = true; };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button
              onClick={() => navigate('/mypage')}
              className="mr-4 p-2 text-gray-600 hover:text-gray-900"
            >
              <ChevronLeftIcon className="w-6 h-6" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">{ENABLE_CMS && cms.title?.trim() ? cms.title : '利用規約'}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <div className="text-center mb-8">
            <DocumentIcon className="w-12 h-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">{cms.title?.trim() || 'バイターズ利用規約'}</h2>
            {!cms.content && (
              <p className="text-gray-600">最終更新日：2024年1月1日</p>
            )}
          </div>
          {loading ? (
            <div className="text-center py-8 text-gray-500">読み込み中...</div>
          ) : (ENABLE_CMS && cms.content) ? (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: cms.content }} />
          ) : (
          <div className="prose max-w-none">
            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第1条（適用）</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                この利用規約（以下「本規約」）は、株式会社バイターズ（以下「当社」）が提供するバイターズサービス（以下「本サービス」）の利用条件を定めるものです。
                登録ユーザーの皆さま（以下「ユーザー」）には、本規約に従って、本サービスをご利用いただきます。
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第2条（利用登録）</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">2.1 登録の申請</h4>
                  <p className="text-gray-700 leading-relaxed">
                    本サービスの利用を希望する方は、本規約に同意の上、当社の定める方法によって利用登録を申請するものとします。
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">2.2 登録の承認</h4>
                  <p className="text-gray-700 leading-relaxed">
                    当社は、利用登録の申請を受けた場合、以下の事由がない限り、原則として登録を承認します。
                  </p>
                  <ul className="list-disc pl-6 text-gray-700 mt-2 space-y-1">
                    <li>利用登録の申請に際して虚偽の事項を届け出た場合</li>
                    <li>本規約に違反したことがある者からの申請である場合</li>
                    <li>その他、当社が利用登録を相当でないと判断した場合</li>
                  </ul>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第3条（ユーザーIDおよびパスワードの管理）</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>ユーザーは、自己の責任において、本サービスのユーザーIDおよびパスワードを適切に管理するものとします。</li>
                <li>ユーザーは、いかなる場合にも、ユーザーIDおよびパスワードを第三者に譲渡または貸与し、もしくは第三者と共用することはできません。</li>
                <li>当社は、ユーザーIDとパスワードの組み合わせが登録情報と一致してログインされた場合には、そのユーザーIDを登録しているユーザー自身による利用とみなします。</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第4条（利用料金および支払方法）</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">4.1 利用料金</h4>
                  <p className="text-gray-700 leading-relaxed">
                    本サービスの基本的な利用は無料です。ただし、一部の機能については有料とする場合があります。
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">4.2 報酬の支払い</h4>
                  <p className="text-gray-700 leading-relaxed">
                    求職者が勤務を完了した場合、事業者から当社を通じて報酬が支払われます。
                    支払いに関する詳細は別途定める支払い規約に従います。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第5条（禁止事項）</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                ユーザーは、本サービスの利用にあたり、以下の行為をしてはなりません。
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>法令または公序良俗に違反する行為</li>
                <li>犯罪行為に関連する行為</li>
                <li>本サービスの内容等、本サービスに含まれる著作権、商標権ほか知的財産権を侵害する行為</li>
                <li>当社、ほかのユーザー、またはその他第三者のサーバーまたはネットワークの機能を破壊したり、妨害したりする行為</li>
                <li>当社のサービスの運営を妨害するおそれのある行為</li>
                <li>ほかのユーザーに関する個人情報等を収集または蓄積する行為</li>
                <li>不正アクセスをし、またはこれを試みる行為</li>
                <li>他のユーザーに成りすます行為</li>
                <li>当社のサービスに関連して、反社会的勢力に対して直接または間接に利益を供与する行為</li>
                <li>その他、当社が不適切と判断する行為</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第6条（本サービスの提供の停止等）</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                当社は、以下のいずれかの事由があると判断した場合、ユーザーに事前に通知することなく本サービスの全部または一部の提供を停止または中断することができるものとします。
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>本サービスにかかるコンピュータシステムの保守点検または更新を行う場合</li>
                <li>地震、落雷、火災、停電または天災などの不可抗力により、本サービスの提供が困難となった場合</li>
                <li>コンピュータまたは通信回線等が事故により停止した場合</li>
                <li>その他、当社が本サービスの提供が困難と判断した場合</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第7条（利用制限および登録抹消）</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                当社は、ユーザーが以下のいずれかに該当する場合には、事前の通知なく、投稿データを削除し、そのユーザーに対して本サービスの全部もしくは一部の利用を制限し、またはユーザーとしての登録を抹消することができるものとします。
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>本規約のいずれかの条項に違反した場合</li>
                <li>登録事項に虚偽の事実があることが判明した場合</li>
                <li>当社からの連絡に対し、一定期間返答がない場合</li>
                <li>本サービスについて、最終の利用から一定期間利用がない場合</li>
                <li>その他、当社が本サービスの利用を適当でないと判断した場合</li>
              </ul>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第8条（退会）</h3>
              <p className="text-gray-700 leading-relaxed">
                ユーザーは、当社の定める退会手続きにより、本サービスから退会できるものとします。
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第9条（保証の否認および免責事項）</h3>
              <div className="space-y-4">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">9.1 保証の否認</h4>
                  <p className="text-gray-700 leading-relaxed">
                    当社は、本サービスに事実上または法律上の瑕疵（安全性、信頼性、正確性、完全性、有効性、特定の目的への適合性、セキュリティなどに関する欠陥、エラーやバグ、権利侵害などを含みます。）がないことを明示的にも黙示的にも保証しておりません。
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">9.2 免責</h4>
                  <p className="text-gray-700 leading-relaxed">
                    当社は、本サービスに起因してユーザーに生じたあらゆる損害について一切の責任を負いません。ただし、本サービスに関する当社とユーザーとの間の契約（本規約を含みます。）が消費者契約法に定める消費者契約となる場合、この免責規定は適用されません。
                  </p>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第10条（サービス内容の変更等）</h3>
              <p className="text-gray-700 leading-relaxed">
                当社は、ユーザーに通知することなく、本サービスの内容を変更しまたは本サービスの提供を中止することができるものとし、これによってユーザーに生じた損害について一切の責任を負いません。
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第11条（利用規約の変更）</h3>
              <p className="text-gray-700 leading-relaxed">
                当社は、必要と判断した場合には、ユーザーに通知することなくいつでも本規約を変更することができるものとします。なお、本規約の変更後、本サービスの利用を開始した場合には、当該ユーザーは変更後の規約に同意したものとみなします。
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第12条（個人情報の取扱い）</h3>
              <p className="text-gray-700 leading-relaxed">
                当社は、本サービスの利用によって取得する個人情報については、当社「プライバシーポリシー」に従い適切に取り扱うものとします。
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第13条（通知または連絡）</h3>
              <p className="text-gray-700 leading-relaxed">
                ユーザーと当社との間の通知または連絡は、当社の定める方法によって行うものとします。当社は、ユーザーから、当社が別途定める方式に従った変更届け出がない限り、現在登録されている連絡先が有効なものとみなして当該連絡先へ通知または連絡を行い、これらは、発信時にユーザーへ到達したものとみなします。
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第14条（権利義務の譲渡の禁止）</h3>
              <p className="text-gray-700 leading-relaxed">
                ユーザーは、当社の書面による事前の承諾なく、利用契約上の地位または本規約に基づく権利もしくは義務を第三者に譲渡し、または担保に供することはできません。
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">第15条（準拠法・裁判管轄）</h3>
              <p className="text-gray-700 leading-relaxed">
                本規約の解釈にあたっては、日本法を準拠法とします。本サービスに関して紛争が生じた場合には、当社の本店所在地を管轄する裁判所を専属的合意管轄とします。
              </p>
            </section>

            <section className="mb-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">お問い合わせ</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700 leading-relaxed mb-2">
                  本規約に関するお問い合わせは、以下までご連絡ください。
                </p>
                <p className="text-gray-700">
                  <strong>株式会社バイターズ</strong><br />
                  メール：legal@baiters.co.jp<br />
                  電話：03-1234-5678（平日10:00-18:00）
                </p>
              </div>
            </section>
          </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;